/**
 * RunsheetContext — replaces RunCtrl's runsheet and selection state, shared by every page.
 *
 * The provider calls useRunsheet(), useStations() and useBackgroundGeo() once, so every
 * page shares one copy of the runsheet, the selected stop, the stations and the position.
 * It has no effects: pages still fetch, start tracking and register the geofence handler.
 * The position is a separate context, so a GPS fix doesn't re-render runsheet readers.
 * It sits inside AppShell, so it mounts at login and is dropped at logout.
 */

import { createContext, useContext, useMemo, useState } from 'react';

import { useBackgroundGeo } from '@/hooks/useBackgroundGeo';
import { useRunsheet } from '@/hooks/useRunsheet';
import { useStations } from '@/hooks/useStations';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { GeoLocation } from '@/hooks/useBackgroundGeo';
import type { RunsheetLine } from '@/types';

interface RunsheetContextValue
  extends
    Pick<
      ReturnType<typeof useRunsheet>,
      | 'runsheet'
      | 'loading'
      | 'fetchRunsheet'
      | 'saveRunsheet'
      | 'updateLine'
      | 'moveLine'
      | 'setRunsheet'
    >,
    Pick<ReturnType<typeof useStations>, 'stations' | 'fetchStations'>,
    // startTracking: the Dashboard starts tracking once at mount. A repeat call replaces the
    // watch, so another page should not need to call it.
    Pick<ReturnType<typeof useBackgroundGeo>, 'startTracking' | 'onGeofenceExit'> {
  /** Id of the selected stop, which drives the list highlight and auto-select */
  currentLine: number | null;
  setCurrentLine: Dispatch<SetStateAction<number | null>>;
  /** Working copy of the selected stop's line, edited by handlers before updateLine */
  current: RunsheetLine | null;
  setCurrent: Dispatch<SetStateAction<RunsheetLine | null>>;
}

interface RunsheetLocationValue {
  location: GeoLocation | null;
}

const RunsheetContext = createContext<RunsheetContextValue | null>(null);
const RunsheetLocationContext = createContext<RunsheetLocationValue | null>(null);

export function RunsheetProvider({ children }: { children: ReactNode }) {
  const { runsheet, loading, fetchRunsheet, saveRunsheet, updateLine, moveLine, setRunsheet } =
    useRunsheet();
  const { stations, fetchStations } = useStations();
  const { location, startTracking, onGeofenceExit } = useBackgroundGeo();
  const [currentLine, setCurrentLine] = useState<number | null>(null);
  const [current, setCurrent] = useState<RunsheetLine | null>(null);

  const value = useMemo<RunsheetContextValue>(
    () => ({
      runsheet,
      loading,
      fetchRunsheet,
      saveRunsheet,
      updateLine,
      moveLine,
      setRunsheet,
      currentLine,
      setCurrentLine,
      current,
      setCurrent,
      stations,
      fetchStations,
      startTracking,
      onGeofenceExit,
    }),
    [
      runsheet,
      loading,
      fetchRunsheet,
      saveRunsheet,
      updateLine,
      moveLine,
      setRunsheet,
      currentLine,
      setCurrentLine,
      current,
      setCurrent,
      stations,
      fetchStations,
      startTracking,
      onGeofenceExit,
    ],
  );

  const locationValue = useMemo<RunsheetLocationValue>(() => ({ location }), [location]);

  return (
    <RunsheetContext.Provider value={value}>
      <RunsheetLocationContext.Provider value={locationValue}>
        {children}
      </RunsheetLocationContext.Provider>
    </RunsheetContext.Provider>
  );
}

export function useRunsheetContext(): RunsheetContextValue {
  const ctx = useContext(RunsheetContext);
  if (!ctx) throw new Error('useRunsheetContext must be used within <RunsheetProvider>');
  return ctx;
}

export function useRunsheetLocation(): RunsheetLocationValue {
  const ctx = useContext(RunsheetLocationContext);
  if (!ctx) throw new Error('useRunsheetLocation must be used within <RunsheetProvider>');
  return ctx;
}
